import { useState } from "react";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import AdminTable from "components/admin/AdminTable";
import { useConfirm } from "components/admin/Feedback";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import SeasonDialog from "./SeasonDialog";
import RewardsDialog from "./RewardsDialog";
import RankingDialog from "./RankingDialog";
import { STATUS, STATUS_COLOR, STATUS_HELP, parseJson, showTime } from "./shared";

/*
 * Danh sách mùa (người dùng: vận hành lên lịch sự kiện):
 *   Lọc nhanh Đang chạy / Sắp tới / Đã xong / Đã huỷ (kèm số lượng) · Tạo mùa.
 *   Mỗi dòng: tên + loại (tên, không mã) · trạng thái có giải thích · thời gian kèm "còn N ngày" ·
 *   người có điểm · thư đã gửi · thao tác chính (Mốc quà, Bảng điểm, Sửa) tách khỏi thao tác phụ / nguy hiểm.
 */

const FILTERS = [
  { key: "all", label: "Tất cả", match: () => true },
  { key: "running", label: "Đang chạy", match: (r) => r.status === 1 },
  { key: "upcoming", label: "Sắp tới", match: (r) => r.status === 0 },
  { key: "done", label: "Đã xong", match: (r) => r.status === 2 || r.status === 3 },
  { key: "cancelled", label: "Đã huỷ", match: (r) => r.status === 9 },
];

function relative(value, prefix) {
  if (!value) return "";
  const ms = new Date(value).getTime() - Date.now();
  if (ms <= 0) return "";
  const hours = Math.floor(ms / 3600000);
  if (hours < 24) return `${prefix} ${Math.max(1, hours)} giờ`;
  return `${prefix} ${Math.floor(hours / 24)} ngày`;
}

function SeasonsPanel() {
  const canEdit = useCan(PERMISSIONS.EVENT_EDIT);
  const confirm = useConfirm();
  const [run, busy] = useRunner();
  const kinds = useFetch(() => api.get("/api/admin/event/kinds"), []);
  const seasons = useFetch(() => api.get("/api/admin/event/seasons"), [], 30000);
  const [dialog, setDialog] = useState(null);
  const [filter, setFilter] = useState("all");
  const close = () => setDialog(null);

  const kindName = (key) => (kinds.data || []).find((k) => k.key === key)?.name || key;
  const rows = seasons.data || [];
  const active = FILTERS.find((f) => f.key === filter) || FILTERS[0];
  const shown = rows.filter(active.match);

  const act = async (path, message, question) => {
    if (question) {
      const ok = await confirm(question);
      if (!ok) return;
    }
    const res = await run(() => api.post(path), message);
    if (res.ok) seasons.reload(true);
  };

  return (
    <>
      {seasons.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Không tải được danh sách mùa: {seasons.error}
        </Alert>
      )}
      <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={2}>
        {FILTERS.map((f) => {
          const count = rows.filter(f.match).length;
          return (
            <Chip
              key={f.key}
              label={`${f.label} (${count})`}
              color={filter === f.key ? "info" : "default"}
              variant={filter === f.key ? "filled" : "outlined"}
              onClick={() => setFilter(f.key)}
            />
          );
        })}
        {canEdit && (
          <MDButton
            color="info"
            variant="gradient"
            sx={{ ml: "auto" }}
            disabled={!kinds.data?.length}
            onClick={() => setDialog({ type: "create" })}
          >
            <Icon>add</Icon>&nbsp;Tạo mùa sự kiện
          </MDButton>
        )}
      </MDBox>
      <AdminTable
        loading={seasons.loading}
        rows={shown}
        rowKey="id"
        emptyText={
          filter === "all"
            ? "Chưa có mùa nào — bấm Tạo mùa sự kiện"
            : `Không có mùa nào "${active.label}"`
        }
        columns={[
          {
            key: "name",
            label: "Mùa",
            render: (row) => {
              const bonus = parseJson(row.config, {})?.modules?.exchange_bonus?.percent;
              return (
                <MDBox>
                  <Tooltip title={`mã mùa ${row.id}`}>
                    <MDTypography variant="button" fontWeight="bold">
                      {row.name}
                    </MDTypography>
                  </Tooltip>
                  <MDBox display="flex" gap={0.5} alignItems="center" flexWrap="wrap" mt={0.25}>
                    <MDTypography variant="caption" color="text">
                      {kindName(row.kind)}
                    </MDTypography>
                    {bonus > 0 && (
                      <Chip size="small" color="warning" label={`đổi vàng +${bonus}%`} />
                    )}
                    {row.ruleId && (
                      <Tooltip title="Mùa này do Quy tắc lặp tự tạo">
                        <Chip size="small" variant="outlined" label="tự lặp" />
                      </Tooltip>
                    )}
                  </MDBox>
                </MDBox>
              );
            },
          },
          {
            key: "status",
            label: "Trạng thái",
            render: (row) => (
              <Tooltip title={STATUS_HELP[row.status] || ""}>
                <Chip
                  size="small"
                  color={STATUS_COLOR[row.status] || "default"}
                  label={
                    (STATUS[row.status] || row.status) +
                    (row.status === 3 && !row.closedAt ? " · còn xem được" : "")
                  }
                />
              </Tooltip>
            ),
          },
          {
            key: "time",
            label: "Thời gian",
            render: (row) => (
              <MDBox>
                <MDTypography variant="caption" display="block">
                  {showTime(row.startAt)} → {showTime(row.endAt)}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {row.status === 0 && relative(row.startAt, "bắt đầu sau")}
                  {row.status === 1 && relative(row.endAt, "còn")}
                </MDTypography>
              </MDBox>
            ),
          },
          {
            key: "scores",
            label: "Người có điểm",
            align: "right",
            render: (row) => (row.scores ? row.scores.toLocaleString("vi-VN") : "-"),
          },
          {
            key: "mails",
            label: "Thư thưởng đã gửi",
            align: "right",
            render: (row) => (row.mails ? row.mails.toLocaleString("vi-VN") : "-"),
          },
          {
            key: "act",
            label: "",
            render: (row) => (
              <MDBox display="flex" flexDirection="column" gap={0.5} alignItems="flex-start">
                <MDBox display="flex" gap={0.5} flexWrap="wrap">
                  <MDButton
                    size="small"
                    variant="outlined"
                    color="info"
                    onClick={() => setDialog({ type: "rewards", row })}
                  >
                    Mốc quà
                  </MDButton>
                  <MDButton
                    size="small"
                    variant="outlined"
                    color="dark"
                    onClick={() => setDialog({ type: "ranking", row })}
                  >
                    Bảng điểm
                  </MDButton>
                  {canEdit && row.status < 3 && (
                    <MDButton
                      size="small"
                      variant="outlined"
                      color="info"
                      onClick={() => setDialog({ type: "edit", row })}
                    >
                      Sửa
                    </MDButton>
                  )}
                </MDBox>
                {canEdit && (
                  <MDBox display="flex" gap={0.5} flexWrap="wrap">
                    {row.status === 3 && (
                      <Tooltip title="Gửi những thư thưởng chưa tới tay người chơi (ví dụ lúc chốt máy chủ bị tắt). Không gửi trùng thư đã gửi.">
                        <MDButton
                          size="small"
                          variant="text"
                          color="success"
                          disabled={busy}
                          onClick={() =>
                            act(
                              `/api/admin/event/seasons/${row.id}/resend`,
                              "Đã gửi lại thư còn thiếu",
                              {
                                title: "Gửi lại thư thưởng còn thiếu",
                                message: `Gửi các thư thưởng của mùa "${row.name}" chưa gửi được. Thư đã gửi sẽ không gửi lại.`,
                                confirmText: "Gửi thư còn thiếu",
                              }
                            )
                          }
                        >
                          Gửi lại thư còn thiếu
                        </MDButton>
                      </Tooltip>
                    )}
                    {row.status !== 9 && (
                      <MDButton
                        size="small"
                        variant="text"
                        color="secondary"
                        disabled={busy}
                        onClick={() =>
                          act(`/api/admin/event/seasons/${row.id}/copy`, "Đã chép sang năm sau", {
                            title: "Chép sang năm sau",
                            message: `Tạo mùa mới giống "${row.name}" (tên, chữ, NPC, vật phẩm), cùng ngày giờ nhưng năm sau. Mùa hiện tại không bị đổi.\n\nLưu ý: mốc quà của mùa mới lấy theo mặc định, KHÔNG chép các mốc đã sửa tay ở mùa này — nhớ mở Mốc quà của mùa mới để kiểm tra.`,
                            confirmText: "Chép sang năm sau",
                          })
                        }
                      >
                        Chép sang năm sau
                      </MDButton>
                    )}
                    {row.status < 3 && (
                      <MDButton
                        size="small"
                        variant="text"
                        color="error"
                        disabled={busy}
                        onClick={() =>
                          act(`/api/admin/event/seasons/${row.id}/cancel`, "Đã huỷ mùa", {
                            title: `Huỷ mùa "${row.name}"`,
                            message: `${
                              row.status === 1
                                ? `Mùa ĐANG CHẠY${
                                    row.scores ? ` (${row.scores} người đã có điểm)` : ""
                                  } sẽ dừng ngay. `
                                : ""
                            }Mùa bị huỷ không chốt thưởng (không ai nhận quà hạng / mốc toàn server) và KHÔNG mở lại được.`,
                            color: "error",
                            confirmText: "Huỷ mùa",
                          })
                        }
                      >
                        Huỷ mùa
                      </MDButton>
                    )}
                  </MDBox>
                )}
              </MDBox>
            ),
          },
        ]}
      />
      {(dialog?.type === "create" || dialog?.type === "edit") && (
        <SeasonDialog
          season={dialog.type === "edit" ? dialog.row : null}
          kinds={kinds.data || []}
          onClose={close}
          onDone={() => seasons.reload()}
        />
      )}
      {dialog?.type === "rewards" && (
        <RewardsDialog season={dialog.row} canEdit={canEdit} onClose={close} />
      )}
      {dialog?.type === "ranking" && <RankingDialog season={dialog.row} onClose={close} />}
    </>
  );
}

export default SeasonsPanel;
